import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, Thermometer, RefreshCw, CheckCircle2 } from 'lucide-react'
import { employeeApi } from '../../lib/employeeApi'
import { compressPhoto } from '../../lib/photo'
import { checkTemperature, parseTemp, QSE_DEFAULTS_VIEW, QseSettingsView } from '../../lib/qseRules'

interface Unit { id: string; nom: string; type: string; tMax: number; tMin: number | null; valeurBoitier: number | null }
interface Line { ecran: string; sonde: string; action: string; degivrage: boolean }

const TITLES: Record<string, string> = {
  hebdo: 'Vérification hebdo des températures',
  matin: 'Relevé des températures — matin',
  soir: 'Relevé des températures — soir',
}

// Relevé de températures au téléphone : un encart par frigo, écran + sonde,
// rouge tout de suite si hors limite, et action corrective obligatoire dans ce cas.
export default function MobileTemperatures() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const moment = ['matin', 'soir', 'hebdo'].includes(params.get('moment') || '') ? params.get('moment')! : 'hebdo'
  const hebdo = moment === 'hebdo'
  const [units, setUnits] = useState<Unit[]>([])
  const [settings, setSettings] = useState<QseSettingsView>(QSE_DEFAULTS_VIEW)
  const [lines, setLines] = useState<Record<string, Line>>({})
  const [photo, setPhoto] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState<{ saved: number; nonConformes: { nom: string }[] } | null>(null)

  useEffect(() => {
    Promise.all([
      employeeApi.get(`/employee/me/cold-units?moment=${moment}`),
      employeeApi.get('/employee/me/qse-settings'),
    ]).then(([u, s]) => {
      setUnits(u.data || [])
      setSettings({ ...QSE_DEFAULTS_VIEW, ...s.data })
      // L'écran du boîtier est pré-rempli s'il a envoyé une valeur il y a moins d'une heure
      const init: Record<string, Line> = {}
      for (const x of u.data || []) init[x.id] = { ecran: x.valeurBoitier != null ? String(x.valeurBoitier) : '', sonde: '', action: '', degivrage: false }
      setLines(init)
    }).catch(() => setError('Chargement impossible, réessayez'))
      .finally(() => setLoading(false))
  }, [moment])

  const set = (id: string, k: keyof Line, v: string | boolean) => setLines(l => ({ ...l, [id]: { ...l[id], [k]: v } }))

  const status = (u: Unit) => {
    const l = lines[u.id] || { ecran: '', sonde: '', action: '', degivrage: false }
    return checkTemperature(u, parseTemp(l.ecran), parseTemp(l.sonde), settings.ecartMaxSonde, l.degivrage)
  }

  const manquants = units.filter(u => {
    const l = lines[u.id]
    if (!l) return true
    // Hebdo ou dégivrage : la sonde est obligatoire ; au quotidien, une valeur suffit
    return hebdo || l.degivrage ? parseTemp(l.sonde) == null : parseTemp(l.sonde) == null && parseTemp(l.ecran) == null
  })
  const sansAction = units.filter(u => { const s = status(u); return !s.vide && !s.conforme && !(lines[u.id]?.action || '').trim() })
  const canSave = units.length > 0 && manquants.length === 0 && sansAction.length === 0 && !saving

  async function pickPhoto(file?: File) {
    if (!file) return
    try { setPhoto(await compressPhoto(file)) } catch { setError('Photo illisible, réessayez') }
  }

  async function save() {
    setSaving(true); setError('')
    try {
      const readings = units.map(u => ({
        coldUnitId: u.id,
        valeurEcran: parseTemp(lines[u.id].ecran),
        valeurSonde: parseTemp(lines[u.id].sonde),
        degivrage: lines[u.id].degivrage,
        actionCorrective: lines[u.id].action.trim() || undefined,
      }))
      const r = await employeeApi.post('/employee/me/temperatures', { moment, readings, ...(photo ? { photo } : {}) })
      setDone(r.data)
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Enregistrement impossible, réessayez')
    } finally { setSaving(false) }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {error && (
        <div className="fixed bottom-4 left-4 right-4 z-50 bg-red-600 text-white text-sm rounded-xl px-4 py-3 shadow-lg" onClick={() => setError('')}>{error}</div>
      )}
      <div className="bg-white border-b px-4 flex items-center gap-3 sticky top-0 z-10" style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: '16px' }}>
        <button onClick={() => navigate('/app/tasks')} className="p-2 rounded-lg hover:bg-gray-100"><ChevronLeft size={20} className="text-gray-600" /></button>
        <div className="flex-1">
          <h1 className="font-bold text-gray-900 flex items-center gap-2"><Thermometer size={18} className="text-cyan-600" /> Températures</h1>
          <p className="text-xs text-gray-500">{TITLES[moment]}</p>
        </div>
      </div>

      {done ? (
        <div className="p-6 text-center">
          <CheckCircle2 size={56} className="mx-auto mb-3 text-green-500" />
          <p className="font-bold text-gray-900 text-lg">{done.saved} frigo(s) enregistré(s)</p>
          {done.nonConformes.length > 0
            ? <p className="text-sm text-red-700 mt-2">{done.nonConformes.length} hors limite : {done.nonConformes.map(n => n.nom).join(', ')}. C'est noté dans le registre QSE avec ton action corrective.</p>
            : <p className="text-sm text-green-700 mt-2">Tout est conforme. La tâche est validée.</p>}
          <button onClick={() => navigate('/app/tasks')} className="mt-6 w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl">Retour à mes tâches</button>
        </div>
      ) : loading ? (
        <div className="text-center py-16 text-gray-400"><RefreshCw size={32} className="mx-auto mb-2 animate-spin text-blue-500" /><p className="text-sm">Chargement...</p></div>
      ) : units.length === 0 ? (
        <div className="p-6 text-center text-gray-500 text-sm">Aucun frigo à relever pour ce moment. Le responsable doit d'abord créer la liste des frigos.</div>
      ) : (
        <div className="p-4 space-y-3 pb-32">
          <div className="bg-cyan-50 border border-cyan-200 rounded-xl px-3 py-2 text-sm text-cyan-900">
            {hebdo
              ? <>Pour chaque frigo : lire l'<b>écran</b>, puis mesurer avec la <b>sonde</b> à cœur d'un produit.</>
              : <>Pour chaque frigo : lire la température et la noter.</>}
          </div>
          {units.map(u => {
            const s = status(u)
            const l = lines[u.id] || { ecran: '', sonde: '', action: '', degivrage: false }
            const border = s.vide ? 'border-gray-200' : s.conforme ? 'border-green-400' : 'border-red-500'
            return (
              <div key={u.id} className={`bg-white rounded-2xl border-2 ${border} p-4 space-y-3`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-bold text-gray-900 text-base leading-snug">{u.nom}</p>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{u.tMin != null ? `${u.tMin} à ` : 'max '}{u.tMax} °C</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-500">Écran {u.valeurBoitier != null && <span className="text-cyan-600">(boîtier)</span>}</span>
                    <input inputMode="decimal" value={l.ecran} onChange={e => set(u.id, 'ecran', e.target.value)} placeholder="°C"
                      className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-3 text-lg font-semibold text-center" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-500">Sonde{hebdo || l.degivrage ? ' *' : ''}</span>
                    <input inputMode="decimal" value={l.sonde} onChange={e => set(u.id, 'sonde', e.target.value)} placeholder="°C"
                      className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-3 text-lg font-semibold text-center" />
                  </label>
                </div>
                <button type="button" onClick={() => set(u.id, 'degivrage', !l.degivrage)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium ${l.degivrage ? 'bg-sky-100 border-sky-400 text-sky-900' : 'bg-white border-gray-200 text-gray-500'}`}>
                  <span className={`w-5 h-5 rounded border flex items-center justify-center ${l.degivrage ? 'bg-sky-600 border-sky-600 text-white' : 'border-gray-300'}`}>{l.degivrage ? '✓' : ''}</span>
                  ❄️ Dégivrage en cours
                </button>
                {l.degivrage && <p className="text-xs text-sky-800">Pendant le dégivrage l'écran remonte : seule la sonde à cœur d'un produit compte.</p>}
                {!s.vide && (s.conforme
                  ? <p className="text-sm font-semibold text-green-700">✓ Conforme</p>
                  : <div className="space-y-2">
                      <p className="text-sm font-semibold text-red-700">✗ Hors limite : {s.raisons.join(', ')}</p>
                      <textarea value={l.action} onChange={e => set(u.id, 'action', e.target.value)} rows={2}
                        placeholder="Action corrective obligatoire (ex. produits déplacés, porte refermée, technicien appelé)"
                        className="w-full border-2 border-red-300 rounded-xl px-3 py-2 text-sm" />
                    </div>)}
              </div>
            )
          })}
          <label className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium ${photo ? 'border-green-400 bg-green-50 text-green-700' : 'border-gray-300 bg-white text-gray-600'}`}>
            {photo ? <img src={photo} alt="" className="w-8 h-8 rounded object-cover" /> : <span>📷</span>}
            {photo ? 'Photo ajoutée' : 'Photo (facultative)'}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => pickPhoto(e.target.files?.[0])} />
          </label>
        </div>
      )}

      {!done && !loading && units.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
          {!canSave && !saving && (
            <p className="text-xs text-gray-500 mb-2 text-center">
              {manquants.length > 0 ? `Encore ${manquants.length} frigo(s) à relever` : `Action corrective à écrire pour ${sansAction.length} frigo(s)`}
            </p>
          )}
          <button onClick={save} disabled={!canSave}
            className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl disabled:opacity-40 text-base">
            {saving ? 'Enregistrement...' : '✓ Enregistrer le relevé'}
          </button>
        </div>
      )}
    </div>
  )
}
